/**
 * Ambient canvas state — a property of the model, not of the pointer.
 *
 * Hover and Selected are deliberately absent: they are transient, live in
 * `hover.store` and reactflow's own `selected`, and are layered on top of the
 * ambient state by CustomNode / CustomEdge. Keeping them out of here is what
 * stops a pointer move from recomputing the whole canvas.
 */
export enum CanvasElementState {
  Default = 'default',
  Faded = 'faded',
  Disabled = 'disabled',
}

export type AmbientStateInput = {
  /** Outside the Focus-mode vertical, or an indirect (phantom) link. */
  isDisabled: boolean;
  /** De-emphasised by an insight tab's own logic, with nothing open. */
  isFaded?: boolean;
  /** A node or a relationship has its drawer open. */
  isOpen: boolean;
  /** This element is the opened one, or an endpoint of the opened relationship. */
  isActive: boolean;
  /**
   * One direct relationship away from the opened entity.
   */
  isRelated?: boolean;
};

export const resolveAmbientState = ({
  isDisabled,
  isFaded = false,
  isOpen,
  isActive,
  isRelated = false,
}: AmbientStateInput): CanvasElementState => {
  if (isDisabled) return CanvasElementState.Disabled;
  if (isActive) return CanvasElementState.Default;
  if (isFaded) return CanvasElementState.Faded;
  if (isOpen && !isRelated) return CanvasElementState.Faded;

  return CanvasElementState.Default;
};

/**
 * The layer scale and the edge hit area are looks, not logic, so they now live
 * beside the other canvas tokens. Re-exported here for one release so existing
 * imports keep working; point new code at `@constants/canvas/canvasTheme`.
 */
export {
  CanvasZIndex,
  EDGE_INTERACTION_WIDTH,
} from '@constants/canvas/canvasTheme';
